// Stage / lineup / assignment FK-aware matching helpers.
//
// Why this file exists
// --------------------
// Three drift patterns recur across web + mobile + edge functions:
//
//   1. "row belongs to stage X" — assignment ↔ stage, lineup ↔ stage,
//      festival_area ↔ stage. Pre-FK era this was a `stage_name`
//      string compare; producer rename silently broke every reader.
//   2. "assignment binds to lineup slot" — historically matched on
//      `(artist_name, stage_name, event_date)` triple; rename of
//      either side desynced the binding.
//   3. "group rows by stage" — building a stage list from lineup or
//      assignments. Pre-FK each label variation made its own group;
//      a producer rename mid-edit fragmented the view.
//
// Post-canonical-migration (`20260504240000_stage_id_canonical`,
// `20260504240200_crew_assignment_lineup_fk`):
//   * `lineup.stage_id`, `crew_assignments.stage_id`,
//     `festival_areas.briefing_stage_id` are UUID FKs to
//     `briefing_stages.id`.
//   * `crew_assignments.lineup_id` is a UUID FK to `lineup.id`.
//
// These helpers encode the FK-first / text-fallback rule once so
// every reader gets the same semantics. Each one has a unit test
// covering the transition cases.

// ── Types ────────────────────────────────────────────────────────

/**
 * Anything that exposes a `stage_id` UUID FK alongside the legacy
 * `stage_name` text cache. Lineup rows, crew_assignments rows,
 * AI-generated assignment payloads.
 */
export type StageBoundRow = {
  stage_id?: string | null;
  stage_name: string;
};

/**
 * Anything that exposes a `lineup_id` UUID FK alongside the legacy
 * `(artist_name, stage_name, event_date)` triple. Crew assignments,
 * notification payloads.
 */
export type LineupBoundRow = StageBoundRow & {
  lineup_id?: string | null;
  artist_name?: string | null;
  event_date: string;
};

/**
 * A lineup row carrying both the stable id and the legacy text
 * triple. The id is the canonical FK target.
 */
export type LineupSlotLike = {
  id?: string | null;
  artist_name: string;
  stage_id?: string | null;
  stage_name: string;
  event_date: string;
};

/**
 * Festival_areas row exposing both the canonical `briefing_stage_id`
 * UUID FK and the legacy text-slug `stage_id` and polygon `name`.
 */
export type FestivalAreaLike = {
  briefing_stage_id?: string | null;
  stage_id?: string | null;
  name?: string | null;
};

// ── Stable group keys ────────────────────────────────────────────

/**
 * Stable identity key for grouping rows by stage.
 *
 * Returns `id:<UUID>` when the FK is set, otherwise `name:<label>`.
 * Different rows that share the same FK collapse onto the same key
 * even if their `stage_name` text caches diverge during a rename.
 */
export function stageGroupKey(row: {
  stage_id?: string | null;
  stage_name?: string | null;
}): string {
  if (row.stage_id) return `id:${row.stage_id}`;
  return `name:${row.stage_name ?? ""}`;
}

/**
 * Canonical key + display label for a stage group, derived from a
 * sample row. Use the result to render a chip / column header that
 * survives renames within a session.
 */
export function stageGroupOf(row: StageBoundRow): {
  key: string;
  stageId: string | null;
  label: string;
} {
  const stageId = row.stage_id ?? null;
  return {
    key: stageGroupKey(row),
    stageId,
    label: row.stage_name ?? "",
  };
}

/**
 * Group rows by stable stage identity. Within each group rows are
 * preserved in input order. Groups themselves come out in the order
 * they were first encountered (callers can re-sort by the producer's
 * intended order via the optional comparator).
 */
export function groupByStage<T extends StageBoundRow>(
  rows: T[],
): Array<{ key: string; stageId: string | null; label: string; rows: T[] }> {
  const order: string[] = [];
  const buckets = new Map<
    string,
    { key: string; stageId: string | null; label: string; rows: T[] }
  >();
  for (const row of rows) {
    const group = stageGroupOf(row);
    if (!buckets.has(group.key)) {
      order.push(group.key);
      buckets.set(group.key, { ...group, rows: [] });
    }
    buckets.get(group.key)!.rows.push(row);
  }
  return order.map((k) => buckets.get(k)!);
}

// ── Stage equality between two rows ──────────────────────────────

/**
 * True when both rows refer to the same stage.
 *
 * If both rows expose a `stage_id` UUID, the FK is definitive — no
 * text fallback. Within a single briefing, briefing_stages has a
 * UNIQUE label per briefing so equal labels imply equal ids; an FK
 * mismatch with equal labels signals stale state, not a match.
 *
 * When at least one side is missing the FK we fall back to
 * `stage_name` equality. Legitimate cases:
 *   - one row is `assignment_type='custom'` (stage_id is NULL by
 *     design; stage_name carries a free-text subject like "Break")
 *   - mid-edit transient rows that haven't been persisted yet
 *   - legacy rows that pre-date the trigger backfill
 */
export function rowsShareStage(a: StageBoundRow, b: StageBoundRow): boolean {
  if (a.stage_id && b.stage_id) return a.stage_id === b.stage_id;
  return a.stage_name === b.stage_name;
}

/**
 * True when a row carrying a stage_name (lineup, assignment) belongs
 * to the given canonical stage record (briefing_stages row + legacy
 * JSONB shape). FK-first when both sides have an id; falls through
 * to label / legacy slug only when at least ONE side is missing the
 * FK — that's the JSONB-shape `stage` argument or a legacy row.
 *
 * If both sides have FKs and they don't match, we return `false`.
 * Within a single briefing, briefing_stages has UNIQUE (briefing_id,
 * label), so equal labels imply equal ids; an FK mismatch with equal
 * labels signals stale state, NOT a match — so we don't fall through
 * to text comparison in that case.
 */
export function rowMatchesStage(
  row: StageBoundRow,
  stage: { id?: string | null; label?: string | null; name?: string | null },
): boolean {
  if (row.stage_id && stage.id) return row.stage_id === stage.id;
  // At least one side missing the FK — accept label / slug match.
  if (stage.label && row.stage_name === stage.label) return true;
  if (stage.name && row.stage_name === stage.name) return true;
  return false;
}

// ── Festival_area ↔ stage matching ───────────────────────────────

/**
 * True when a polygon (festival_areas row) covers the given stage.
 * FK-first via `briefing_stage_id`; legacy slug + name fallback.
 */
export function areaMatchesStage(
  area: FestivalAreaLike,
  stage: { id?: string | null; label?: string | null; name?: string | null },
): boolean {
  if (area.briefing_stage_id && stage.id) {
    return area.briefing_stage_id === stage.id;
  }
  if (stage.id && area.stage_id === stage.id) return true; // legacy slug coincidence
  if (stage.label && area.name && area.name.toLowerCase() === stage.label.toLowerCase()) {
    return true;
  }
  if (stage.name && area.name && area.name.toLowerCase() === stage.name.toLowerCase()) {
    return true;
  }
  return false;
}

/**
 * Find the stage-zone polygon (festival_areas.area_type === 'stage_zone')
 * that covers the given stage, or null. Used by the crew app to turn an
 * assignment's stage into a "view on the map" jump. FK-first via
 * `areaMatchesStage`; only stage-zone areas are considered (a media
 * office / catering polygon is never a stage).
 */
export function findStageZoneForStage<A extends FestivalAreaLike & { area_type?: string | null }>(
  areas: A[],
  stage: { id?: string | null; label?: string | null; name?: string | null },
): A | null {
  if (!stage.id && !stage.label && !stage.name) return null;
  return areas.find((a) => a.area_type === "stage_zone" && areaMatchesStage(a, stage)) ?? null;
}

// ── Lineup binding for crew_assignments ──────────────────────────

/**
 * Find the lineup slot a crew_assignment binds to.
 *
 * Order:
 *   1. `assignment.lineup_id` matches a slot's `id` — the canonical
 *      FK, set by trigger from migration `20260504240200`.
 *   2. Filming-window inside slot's set window with same stage_id +
 *      event_date + artist_name — FK on stage, text on artist (no
 *      artists table to FK against).
 *   3. Legacy text triple `(artist_name, stage_name, event_date)` —
 *      for assignments / lineup rows that haven't backfilled.
 *
 * Returns the matched slot or null. Mirrors the BEFORE INSERT
 * trigger `set_crew_assignment_lineup_id` server-side, so client
 * bindings stay consistent with what the DB resolves on save.
 */
export function findLineupSlotForAssignment<T extends LineupSlotLike>(
  assignment: LineupBoundRow,
  lineup: T[],
  options: {
    /** When true, only the FK is consulted — no fallbacks. */
    fkOnly?: boolean;
  } = {},
): T | null {
  if (assignment.lineup_id) {
    const fkHit = lineup.find((l) => l.id === assignment.lineup_id);
    if (fkHit) return fkHit;
    if (options.fkOnly) return null;
  }
  if (options.fkOnly) return null;

  // Stage-id match path: same stage_id, same date, same artist name.
  if (assignment.stage_id && assignment.artist_name) {
    const stageMatch = lineup.find(
      (l) =>
        l.stage_id === assignment.stage_id &&
        l.event_date === assignment.event_date &&
        l.artist_name === assignment.artist_name,
    );
    if (stageMatch) return stageMatch;
  }

  // Legacy text triple.
  if (assignment.artist_name) {
    return (
      lineup.find(
        (l) =>
          l.artist_name === assignment.artist_name &&
          l.stage_name === assignment.stage_name &&
          l.event_date === assignment.event_date,
      ) ?? null
    );
  }
  return null;
}

/**
 * True when two crew_assignment-shaped rows refer to the same logical
 * assignment (used by drag-drop and dedup helpers). FK-first on
 * `id` then `lineup_id` then `stage_id`; falls through to text
 * comparison.
 */
export function assignmentsLikelySame(
  a: { id?: string | null } & LineupBoundRow,
  b: { id?: string | null } & LineupBoundRow,
): boolean {
  if (a.id && b.id) return a.id === b.id;
  if (a.lineup_id && b.lineup_id && a.lineup_id !== b.lineup_id) return false;
  if (a.lineup_id && b.lineup_id && a.lineup_id === b.lineup_id) return true;
  if (a.stage_id && b.stage_id && a.stage_id !== b.stage_id) return false;
  if (a.event_date !== b.event_date) return false;
  return (a.artist_name ?? null) === (b.artist_name ?? null) && a.stage_name === b.stage_name;
}
